/**
 * Cart Address Map (CAM)
 * ─────────────────────────────────────────────
 * Google Maps address picker for the cart page.
 *
 * FLOW:
 *   1. User clicks checkout on cart page
 *   2. NOT logged in → redirect to /account/login?return_url=/cart
 *   3. After login → auto-redirected back to /cart
 *   4. On cart page load (logged-in user) → auto-open modal
 *   5. Modal asks: "Activate your location?" (browser GPS prompt)
 *      • YES → browser asks permission → center map on GPS coords
 *      • NO  → search bar is focused, user types address manually
 *   6. Map shows pin point + reverse-geocoded address + lat/lng
 *   7. User can drag map or search to adjust pin
 *   8. User clicks "Save Address" → modal closes (and hit api graphql to save address in backend shopify)
 */

(function () {
  'use strict';

  /* ── CONFIG ─────────────────────────────────── */
  var GMAPS_API_KEY = 'AIzaSyBmnh6diibvrDq-nLO5UAXInBBdl9NDJlE';
  var DEFAULT_CENTER = { lat: -6.2088, lng: 106.8456 }; // Jakarta fallback
  var DEFAULT_ZOOM = 15;
  var RETURN_FLAG = 'cam_show_map'; // sessionStorage key for post-login auto-open
  var BACKEND_URL = 'https://backend-address-minuman.vercel.app'; // Backend API base URL

  /* ── PROVINCE NORMALIZATION MAP ──────────────── */
  // Google Maps returns long province names; Shopify expects short canonical names.
  var PROVINCE_MAP = {
    // Indonesian names
    'daerah khusus ibukota jakarta': 'Jakarta',
    'dki jakarta': 'Jakarta',
    'daerah istimewa yogyakarta': 'Yogyakarta',
    'di yogyakarta': 'Yogyakarta',
    'nanggroe aceh darussalam': 'Aceh',
    'kepulauan bangka belitung': 'Bangka Belitung',
    'nusa tenggara barat': 'Nusa Tenggara Barat',
    'nusa tenggara timur': 'Nusa Tenggara Timur',
    'kalimantan utara': 'Kalimantan Utara',
    'kalimantan timur': 'Kalimantan Timur',
    'kalimantan selatan': 'Kalimantan Selatan',
    'kalimantan tengah': 'Kalimantan Tengah',
    'kalimantan barat': 'Kalimantan Barat',
    'sulawesi utara': 'Sulawesi Utara',
    'sulawesi tengah': 'Sulawesi Tengah',
    'sulawesi selatan': 'Sulawesi Selatan',
    'sulawesi tenggara': 'Sulawesi Tenggara',
    'sulawesi barat': 'Sulawesi Barat',
    'sumatra utara': 'Sumatra Utara',
    'sumatera utara': 'Sumatra Utara',
    'sumatra barat': 'Sumatra Barat',
    'sumatera barat': 'Sumatra Barat',
    'sumatra selatan': 'Sumatra Selatan',
    'sumatera selatan': 'Sumatra Selatan',
    'jawa barat': 'Jawa Barat',
    'jawa tengah': 'Jawa Tengah',
    'jawa timur': 'Jawa Timur',
    'kepulauan riau': 'Kepulauan Riau',
    'maluku utara': 'Maluku Utara',
    'papua barat': 'Papua Barat',
    // English names (returned by Google Maps)
    'aceh': 'Aceh',
    'bali': 'Bali',
    'bengkulu': 'Bengkulu',
    'gorontalo': 'Gorontalo',
    'jakarta': 'Jakarta',
    'jambi': 'Jambi',
    'lampung': 'Lampung',
    'maluku': 'Maluku',
    'north kalimantan': 'Kalimantan Utara',
    'east kalimantan': 'Kalimantan Timur',
    'south kalimantan': 'Kalimantan Selatan',
    'central kalimantan': 'Kalimantan Tengah',
    'west kalimantan': 'Kalimantan Barat',
    'north maluku': 'Maluku Utara',
    'north sulawesi': 'Sulawesi Utara',
    'central sulawesi': 'Sulawesi Tengah',
    'south sulawesi': 'Sulawesi Selatan',
    'southeast sulawesi': 'Sulawesi Tenggara',
    'west sulawesi': 'Sulawesi Barat',
    'north sumatra': 'Sumatra Utara',
    'west sumatra': 'Sumatra Barat',
    'south sumatra': 'Sumatra Selatan',
    'bangka belitung islands': 'Bangka Belitung',
    'bangka-belitung islands': 'Bangka Belitung',
    'riau islands': 'Kepulauan Riau',
    'riau': 'Riau',
    'west java': 'Jawa Barat',
    'central java': 'Jawa Tengah',
    'east java': 'Jawa Timur',
    'west nusa tenggara': 'Nusa Tenggara Barat',
    'east nusa tenggara': 'Nusa Tenggara Timur',
    'west papua': 'Papua Barat',
    'papua': 'Papua',
    'special capital region of jakarta': 'Jakarta',
    'special region of yogyakarta': 'Yogyakarta',
    'yogyakarta': 'Yogyakarta',
  };

  function normalizeProvince(rawProvince) {
    if (!rawProvince) return '';
    var key = rawProvince.toLowerCase().trim();
    if (PROVINCE_MAP[key]) return PROVINCE_MAP[key];
    // Not in map — return as-is to preserve original name (avoid mangling)
    return rawProvince;
  }

  /* ── ADDRESS SHORTENER ──────────────────────── */
  // Strip district, city, province, postal code, and country from the formatted address
  // since those are already sent as separate fields.
  function shortenAddress(formatted, district, city, province, zip, country) {
    if (!formatted) return '';
    var short = formatted;
    if (country) short = short.replace(new RegExp(',?\\s*' + country.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*$', 'i'), '');
    if (zip) short = short.replace(new RegExp(',?\\s*' + zip.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*', 'g'), '');
    if (province) short = short.replace(new RegExp(',?\\s*' + province.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*', 'i'), '');
    if (city) short = short.replace(new RegExp(',?\\s*' + city.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*', 'i'), '');
    // Strip district with common prefixes: "Kec. X", "Kecamatan X", or just "X"
    if (district) {
      var distEsc = district.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      short = short.replace(new RegExp(',?\\s*(?:Kec\\.?|Kecamatan)?\\s*' + distEsc + '\\s*', 'i'), '');
    }
    short = short.replace(/,\s*$/, '').replace(/^\s*,\s*/, '').trim();
    return short || formatted;
  }

  /* ── STATE ──────────────────────────────────── */
  var map = null;
  var geocoder = null;
  var marker = null;
  var selectedAddress = { formatted: '', lat: 0, lng: 0, extra: '', district: '', city: '', province: '', zip: '', country: '' };
  var mapsLoaded = false;
  var scrollPos = 0;

  /* ── FORM-FILL MODE STATE ───────────────────── */
  // When non-null, save action fills form fields instead of calling backend API
  var formFillPrefix = null; // e.g. "address-new" for new address form, or "address-12345" for edit
  var formFillCallback = null; // optional callback after form fill

  /* ── HELPERS ────────────────────────────────── */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return (ctx || document).querySelectorAll(sel); }

  function showToast(msg, type) {
    var t = qs('.cam-toast');
    if (!t) {
      t = document.createElement('div');
      t.className = 'cam-toast';
      document.body.appendChild(t);
    }
    t.textContent = msg;
    t.className = 'cam-toast' + (type ? ' cam-toast--' + type : '');
    requestAnimationFrame(function () { t.classList.add('is-active'); });
    clearTimeout(t._timer);
    t._timer = setTimeout(function () { t.classList.remove('is-active'); }, 3000);
  }

  function lockScroll() {
    scrollPos = window.pageYOffset;
    document.body.classList.add('cam-no-scroll');
    document.body.style.top = -scrollPos + 'px';
  }

  function unlockScroll() {
    document.body.classList.remove('cam-no-scroll');
    document.body.style.top = '';
    window.scrollTo(0, scrollPos);
  }

  /* ── GOOGLE MAPS LOADER ─────────────────────── */
  function loadGoogleMaps() {
    return new Promise(function (resolve, reject) {
      if (mapsLoaded && window.google && window.google.maps) { resolve(); return; }
      if (document.getElementById('cam-gmaps-script')) {
        var poll = setInterval(function () {
          if (window.google && window.google.maps) { clearInterval(poll); mapsLoaded = true; resolve(); }
        }, 100);
        return;
      }
      window._camMapsReady = function () { mapsLoaded = true; resolve(); };
      var s = document.createElement('script');
      s.id = 'cam-gmaps-script';
      s.src = 'https://maps.googleapis.com/maps/api/js?key=' + GMAPS_API_KEY + '&libraries=places&callback=_camMapsReady';
      s.async = true;
      s.defer = true;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  /* ── GEOLOCATION ────────────────────────────── */
  function getCurrentPosition() {
    return new Promise(function (resolve, reject) {
      if (!navigator.geolocation) { reject(new Error('Geolocation not supported')); return; }
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      });
    });
  }

  /* ── POPUP HTML ─────────────────────────────── */
  function createPopupHTML() {
    return '' +
    '<div class="cam-overlay" id="cam-overlay" aria-hidden="true" role="dialog" aria-label="Delivery address picker">' +
      '<div class="cam-modal" role="document">' +
        '<div class="cam-handle"></div>' +

        '<!-- Header -->' +
        '<div class="cam-header">' +
          '<h2 class="cam-header__title">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>' +
            ' Delivery Address' +
          '</h2>' +
          '<button class="cam-close" id="cam-close" aria-label="Close">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
          '</button>' +
        '</div>' +

        '<div class="cam-body">' +

          '<!-- STEP: Location Permission -->' +
          '<div class="cam-step is-active" id="cam-step-permission">' +
            '<div class="cam-steps"><span class="cam-steps__dot is-active"></span><span class="cam-steps__dot"></span></div>' +
            '<div class="cam-permission">' +
              '<div class="cam-permission__icon">' +
                '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>' +
              '</div>' +
              '<h3 class="cam-permission__title">Where should we deliver?</h3>' +
              '<p class="cam-permission__desc">Allow us to detect your current location for a faster experience, or search your address manually.</p>' +
              '<div class="cam-permission__actions">' +
                '<button class="cam-btn cam-btn--primary" id="cam-btn-gps">' +
                  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>' +
                  ' Use My Current Location' +
                '</button>' +
                '<button class="cam-btn cam-btn--secondary" id="cam-btn-manual">' +
                  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>' +
                  ' Search Address Manually' +
                '</button>' +
              '</div>' +
            '</div>' +
          '</div>' +

          '<!-- STEP: Map Picker -->' +
          '<div class="cam-step" id="cam-step-map">' +
            '<div class="cam-steps"><span class="cam-steps__dot"></span><span class="cam-steps__dot is-active"></span></div>' +
            '<div class="cam-search">' +
              '<svg class="cam-search__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>' +
              '<input type="text" class="cam-search__input" id="cam-search-input" placeholder="Search your address..." autocomplete="off">' +
            '</div>' +            '<p class="cam-gps-hint" id="cam-gps-hint" style="display:none;margin:6px 4px;font-size:12px;color:#888;text-align:center;">' +
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:13px;height:13px;vertical-align:-2px;margin-right:3px;"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>' +
              'GPS not precise enough? Search your address above for a more accurate result.' +
            '</p>' +            '<div class="cam-map-wrap">' +
              '<div class="cam-map" id="cam-map"></div>' +
              '<div class="cam-map-hint" id="cam-map-hint">' +
                '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:16px;height:16px;vertical-align:middle;margin-right:4px;"><path d="M15 15l-2 5L9 9l11 4-5 2z"/></svg>' +
                'Tap on the map to set your exact location' +
              '</div>' +
              '<button class="cam-recenter" id="cam-recenter" title="Re-center to my location">' +
                '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>' +
              '</button>' +
            '</div>' +
            '<div class="cam-address-info" id="cam-address-info">' +
              '<p class="cam-address-info__label">Selected Address</p>' +
              '<p class="cam-address-info__text" id="cam-address-text">Tap the map or search to pick your address...</p>' +
              '<div class="cam-address-info__coords">' +
                '<span>Lat: <strong id="cam-lat">&mdash;</strong></span>' +
                '<span>Lng: <strong id="cam-lng">&mdash;</strong></span>' +
              '</div>' +
            '</div>' +
            '<div class="cam-extra">' +
              '<label class="cam-extra__label" for="cam-extra-input">Additional details</label>' +
              '<p class="cam-extra__hint">Correct the house number if inaccurate, or add floor, unit, landmark, etc.</p>' +
              '<textarea class="cam-extra__input" id="cam-extra-input" rows="2" placeholder="e.g. No. 25A RT 03/RW 05, Floor 3, near the main lobby"></textarea>' +
            '</div>' +
          '</div>' +

          '<!-- STEP: Loading -->' +
          '<div class="cam-step" id="cam-step-loading">' +
            '<div class="cam-loading">' +
              '<div class="cam-loading__spinner"></div>' +
              '<p class="cam-loading__text">Detecting your location...</p>' +
            '</div>' +
          '</div>' +

        '</div>' +

        '<!-- Footer -->' +
        '<div class="cam-footer" id="cam-footer" style="display:none;">' +
          '<button class="cam-btn cam-btn--primary" id="cam-save-btn">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>' +
            ' Save Address' +
          '</button>' +
        '</div>' +

      '</div>' +
    '</div>';
  }

  /* ── PLACE / MOVE MARKER ────────────────────── */
  function placeMarker(latLng, zoom) {
    if (!map) return;
    var pos = (latLng instanceof google.maps.LatLng)
      ? latLng
      : new google.maps.LatLng(latLng.lat, latLng.lng);

    if (!marker) {
      marker = new google.maps.Marker({
        position: pos,
        map: map,
        draggable: true,
        icon: {
          path: 'M12 0C7.31 0 3.5 3.81 3.5 8.5c0 6.38 8.5 15.5 8.5 15.5s8.5-9.12 8.5-15.5C20.5 3.81 16.69 0 12 0zm0 12a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7z',
          fillColor: '#d32234',
          fillOpacity: 1,
          strokeColor: '#fff',
          strokeWeight: 1.5,
          scale: 1.6,
          anchor: new google.maps.Point(12, 24),
        },
        animation: google.maps.Animation.DROP,
      });
      // Reverse-geocode after user drags the marker
      marker.addListener('dragend', function () {
        var p = marker.getPosition();
        map.panTo(p);
        reverseGeocode(p.lat(), p.lng());
      });
    } else {
      marker.setPosition(pos);
    }

    map.panTo(pos);
    if (zoom) map.setZoom(zoom);
    reverseGeocode(pos.lat(), pos.lng());
  }

  /* ── INIT MAP ───────────────────────────────── */
  function initMap(center) {
    var mapEl = qs('#cam-map');
    if (!mapEl) return;

    var startCenter = center || DEFAULT_CENTER;

    map = new google.maps.Map(mapEl, {
      center: startCenter,
      zoom: DEFAULT_ZOOM,
      disableDefaultUI: true,
      zoomControl: true,
      gestureHandling: 'greedy',
      styles: [
        { featureType: 'poi', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', stylers: [{ visibility: 'off' }] },
      ],
    });

    geocoder = new google.maps.Geocoder();
    marker = null; // reset for fresh map

    // Place initial marker
    placeMarker(startCenter);

    // Click on map → move marker to that spot
    map.addListener('click', function (e) {
      placeMarker(e.latLng);
    });

    // --- Auto-suggest search with debounce ---
    var input = qs('#cam-search-input');
    if (input) {
      var acService = new google.maps.places.AutocompleteService();
      var placesService = new google.maps.places.PlacesService(map);

      var suggestBox = document.createElement('div');
      suggestBox.className = 'cam-suggest';
      input.parentNode.appendChild(suggestBox);

      var debounceTimer;
      var lastQuery = '';

      input.addEventListener('input', function () {
        clearTimeout(debounceTimer);
        var val = input.value.trim();

        // Need at least 3 chars to start searching
        if (val.length < 3) {
          suggestBox.innerHTML = '';
          suggestBox.style.display = 'none';
          lastQuery = '';
          return;
        }

        // Skip if same query (avoids duplicate API calls)
        if (val === lastQuery) return;

        debounceTimer = setTimeout(function () {
          lastQuery = val;
          acService.getPlacePredictions(
            { input: val, componentRestrictions: { country: 'id' } },
            function (predictions, status) {
              suggestBox.innerHTML = '';
              if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
                suggestBox.style.display = 'none';
                return;
              }
              predictions.forEach(function (p) {
                var row = document.createElement('div');
                row.className = 'cam-suggest__item';
                row.innerHTML =
                  '<svg class="cam-suggest__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>' +
                  '<div class="cam-suggest__text">' +
                    '<span class="cam-suggest__main">' + (p.structured_formatting.main_text || '') + '</span>' +
                    '<span class="cam-suggest__sub">' + (p.structured_formatting.secondary_text || '') + '</span>' +
                  '</div>';
                // Use mousedown so it fires before blur hides the dropdown
                row.addEventListener('mousedown', function (evt) {
                  evt.preventDefault();
                  input.value = p.description;
                  suggestBox.innerHTML = '';
                  suggestBox.style.display = 'none';
                  placesService.getDetails(
                    { placeId: p.place_id, fields: ['geometry'] },
                    function (place, s2) {
                      if (s2 === google.maps.places.PlacesServiceStatus.OK && place.geometry) {
                        placeMarker(place.geometry.location, 17);
                      }
                    }
                  );
                });
                suggestBox.appendChild(row);
              });
              suggestBox.style.display = 'block';
            }
          );
        }, 300);
      });

      // Hide on blur
      input.addEventListener('blur', function () {
        setTimeout(function () { suggestBox.style.display = 'none'; }, 150);
      });
      // Re-show on focus if results exist
      input.addEventListener('focus', function () {
        if (suggestBox.children.length > 0) suggestBox.style.display = 'block';
      });
      // Hide suggestions on map interaction
      map.addListener('dragstart', function () { suggestBox.style.display = 'none'; });
    }
  }

  /* ── REVERSE GEOCODE ────────────────────────── */
  function extractAddressComponent(components, type) {
    for (var i = 0; i < components.length; i++) {
      if (components[i].types.indexOf(type) !== -1) {
        return components[i].long_name;
      }
    }
    return '';
  }

  function parseAddressComponents(components) {
    // District / Kecamatan: Google uses these types for Indonesian sub-districts
    var district =
      extractAddressComponent(components, 'administrative_area_level_3') ||
      extractAddressComponent(components, 'sublocality_level_1') ||
      extractAddressComponent(components, 'sublocality') ||
      extractAddressComponent(components, 'administrative_area_level_4') ||
      '';

    // City / Kabupaten
    var city =
      extractAddressComponent(components, 'administrative_area_level_2') ||
      extractAddressComponent(components, 'locality') ||
      '';

    // Province
    var province =
      extractAddressComponent(components, 'administrative_area_level_1') ||
      '';

    // Postal code
    var zip = extractAddressComponent(components, 'postal_code') || '';

    // Country
    var country = extractAddressComponent(components, 'country') || 'Indonesia';

    return {
      district: district,
      city: city,
      province: province,
      zip: zip,
      country: country,
    };
  }

  function reverseGeocode(lat, lng) {
    if (!geocoder) return;

    var latEl  = qs('#cam-lat');
    var lngEl  = qs('#cam-lng');
    var textEl = qs('#cam-address-text');

    if (latEl) latEl.textContent = lat.toFixed(6);
    if (lngEl) lngEl.textContent = lng.toFixed(6);
    selectedAddress.lat = lat;
    selectedAddress.lng = lng;

    geocoder.geocode({ location: { lat: lat, lng: lng } }, function (results, status) {
      if (status === 'OK' && results[0]) {
        selectedAddress.formatted = results[0].formatted_address;
        if (textEl) textEl.textContent = results[0].formatted_address;

        // Parse address components for district, city, province, zip, country
        if (results[0].address_components) {
          var parsed = parseAddressComponents(results[0].address_components);
          var rawProvince = parsed.province;
          parsed.province = normalizeProvince(parsed.province);
          selectedAddress.district     = parsed.district;
          selectedAddress.city         = parsed.city;
          selectedAddress.province     = parsed.province;
          selectedAddress.zip          = parsed.zip;
          selectedAddress.country      = parsed.country;
          selectedAddress._rawProvince = rawProvince; // keep original for shortening later
          console.log('[CAM] Parsed address components:', parsed);
          console.log('[CAM] Province normalized:', rawProvince, '→', parsed.province);
        }
      } else {
        if (textEl) textEl.textContent = 'Address not found. Try moving the pin.';
      }
    });
  }

  /* ── MODAL SHOW / HIDE ─────────────────────── */
  function ensurePopupExists() {
    if (!qs('#cam-overlay')) {
      var wrap = document.createElement('div');
      wrap.innerHTML = createPopupHTML();
      document.body.appendChild(wrap.firstElementChild);
      bindEvents();
    }
  }

  function openModal() {
    ensurePopupExists();
    lockScroll();
    var overlay = qs('#cam-overlay');
    overlay.classList.add('is-active');
    overlay.setAttribute('aria-hidden', 'false');
  }

  function closeModal() {
    var overlay = qs('#cam-overlay');
    if (!overlay) return;
    overlay.classList.remove('is-active');
    overlay.setAttribute('aria-hidden', 'true');
    unlockScroll();
  }

  function showStep(name) {
    var steps = qsa('.cam-step');
    for (var i = 0; i < steps.length; i++) { steps[i].classList.remove('is-active'); }
    var step = qs('#cam-step-' + name);
    if (step) step.classList.add('is-active');
    var footer = qs('#cam-footer');
    if (footer) footer.style.display = (name === 'map') ? '' : 'none';
  }

  /* ── GPS FLOW ───────────────────────────────── */
  function handleGPS() {
    showStep('loading');
    loadGoogleMaps().then(function () {
      return getCurrentPosition();
    }).then(function (pos) {
      showStep('map');
      initMap({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      var gpsHint = qs('#cam-gps-hint');
      if (gpsHint) gpsHint.style.display = '';
      showToast('Location detected!', 'success');
    }).catch(function (err) {
      console.warn('GPS error:', err);
      showStep('map');
      initMap(DEFAULT_CENTER);
      showToast('Could not detect location. Please search manually.', 'error');
      setTimeout(function () { var i = qs('#cam-search-input'); if (i) i.focus(); }, 400);
    });
  }

  /* ── MANUAL FLOW ────────────────────────────── */
  function handleManual() {
    showStep('loading');
    loadGoogleMaps().then(function () {
      showStep('map');
      initMap(DEFAULT_CENTER);
      var gpsHint = qs('#cam-gps-hint');
      if (gpsHint) gpsHint.style.display = 'none';
      setTimeout(function () { var i = qs('#cam-search-input'); if (i) i.focus(); }, 400);
    }).catch(function (err) {
      console.error('Maps load failed:', err);
      showToast('Failed to load maps. Please try again.', 'error');
      showStep('permission');
    });
  }

  /* ── RECENTER ───────────────────────────────── */
  function handleRecenter() {
    getCurrentPosition().then(function (pos) {
      if (map) {
        placeMarker({ lat: pos.coords.latitude, lng: pos.coords.longitude }, DEFAULT_ZOOM);
      }
      showToast('Re-centered to your location', 'success');
    }).catch(function () {
      showToast('Could not detect location', 'error');
    });
  }

  /* ── SAVE ADDRESS ────────────────────────────── */
  function handleSave() {
    if (!selectedAddress.formatted) {
      showToast('Please select an address on the map first', 'error');
      return;
    }
    var extraInput = qs('#cam-extra-input');
    selectedAddress.extra = extraInput ? extraInput.value.trim() : '';

    // ── FORM-FILL MODE: fill Shopify address form fields ──
    if (formFillPrefix) {
      handleFormFillSave();
      return;
    }

    // ── DEFAULT MODE: Save via Backend API ──
    // Get customer email from Liquid-injected global
    var customerEmail = window._camCustomerEmail || '';
    if (!customerEmail) {
      showToast('Customer email not found. Please re-login.', 'error');
      return;
    }

    // UI feedback — disable save button and show loading
    var saveBtn = qs('#cam-save-btn');
    if (saveBtn) {
      saveBtn.disabled = true;
      saveBtn.innerHTML =
        '<span class="cam-btn-spinner"></span> Saving...';
    }

    // Shorten address for checkout (strip district/city/province/zip/country — they go in separate fields)
    var shortAddr = shortenAddress(
      selectedAddress.formatted,
      selectedAddress.district,
      selectedAddress.city,
      selectedAddress._rawProvince || selectedAddress.province,
      selectedAddress.zip,
      selectedAddress.country
    );
    // Combine shortened address + extra details with parentheses format
    var finalFormatted = shortAddr;
    if (selectedAddress.extra) {
      finalFormatted = finalFormatted + '  (' + selectedAddress.extra + ')';
    }

    var payload = {
      email: customerEmail,
      address: {
        formatted: finalFormatted,
        lat: selectedAddress.lat,
        lng: selectedAddress.lng,
        extra: '',
        district: selectedAddress.district,
        city: selectedAddress.city,
        province: selectedAddress.province,
        zip: selectedAddress.zip,
        country: selectedAddress.country,
      },
    };

    var apiEditId = _editAddressId;
    _editAddressId = null; // reset before async
    if (apiEditId) payload.addressId = apiEditId;

    var apiUrl = apiEditId
      ? BACKEND_URL + '/api/address/update'
      : BACKEND_URL + '/api/address/save';

    fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (data.success) {
          console.log('[CAM] Address saved/updated to Shopify:', data);

          // Mark that customer now has an address
          window._camCustomerHasAddress = true;

          var afterSave = function () {
            showToast(apiEditId ? 'Address updated!' : 'Address saved!', 'success');
            closeModal();
            // Clear saved selection so the new default is used on reload
            try { localStorage.removeItem('cam_selected_address_id'); } catch(ex) {}
            setTimeout(function () { window.location.reload(); }, 600);
          };

          // Auto-set new address as default
          var newAddrId = data.address && data.address.id;
          if (newAddrId && window._camCustomerEmail) {
            fetch(BACKEND_URL + '/api/address/set-default', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: window._camCustomerEmail, addressId: newAddrId }),
            })
              .catch(function () {}) // silently ignore set-default errors
              .finally(afterSave);
          } else {
            afterSave();
          }
        } else {
          console.error('[CAM] Save failed:', data);
          showToast(data.error || 'Failed to save address', 'error');
        }
      })
      .catch(function (err) {
        console.error('[CAM] Network error:', err);
        showToast('Network error. Please try again.', 'error');
      })
      .finally(function () {
        // Restore save button
        if (saveBtn) {
          saveBtn.disabled = false;
          saveBtn.innerHTML =
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>' +
            ' Save Address';
        }
      });
  }

  /* ── FORM-FILL SAVE: Populate Shopify address form ── */
  function handleFormFillSave() {
    var prefix = formFillPrefix;
    console.log('[CAM] Form-fill mode – populating fields with prefix:', prefix);

    // Shorten address for the address1 field
    var shortAddr = shortenAddress(
      selectedAddress.formatted,
      selectedAddress.district,
      selectedAddress.city,
      selectedAddress._rawProvince || selectedAddress.province,
      selectedAddress.zip,
      selectedAddress.country
    );
    if (selectedAddress.extra) {
      shortAddr = shortAddr + '  (' + selectedAddress.extra + ')';
    }

    // Helper to set a form field value and trigger 'is-filled' class
    function setField(fieldName, value) {
      var el = document.getElementById(prefix + '[' + fieldName + ']');
      if (!el) {
        console.warn('[CAM] Form field not found:', prefix + '[' + fieldName + ']');
        return;
      }
      if (el.tagName === 'SELECT') {
        // For select elements, find and set the matching option
        for (var i = 0; i < el.options.length; i++) {
          if (el.options[i].value === value || el.options[i].text === value) {
            el.selectedIndex = i;
            break;
          }
        }
      } else {
        el.value = value;
      }
      // Trigger is-filled class for floating labels
      if (value) {
        el.classList.add('is-filled');
      }
      // Dispatch events so other JS (e.g. createaddress.js) can react
      el.dispatchEvent(new Event('change', { bubbles: true }));
      el.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Fill the form fields
    setField('address1', shortAddr);
    setField('address2', selectedAddress.district); // kecamatan
    setField('city', selectedAddress.city);
    setField('province', selectedAddress.province);
    setField('zip', selectedAddress.zip);
    setField('country', selectedAddress.country || 'Indonesia');
    setField('company', selectedAddress.formatted); // full formatted address stored in company

    // Kirim ke backend custom
    var backendPayload = {
      email: window._camCustomerEmail || 'hafidz@expatify.co.id',
       address: {   
           formatted: shortAddr,
         address2: selectedAddress.district,
         city: selectedAddress.city,
         province: selectedAddress.province,
         zip: selectedAddress.zip,
         country: selectedAddress.country || 'Indonesia',
         company: selectedAddress.formatted,
         lat: selectedAddress.lat,
         lng: selectedAddress.lng,
         source: 'form-autofill-map'
       },
    };
    fetch(BACKEND_URL + '/api/address/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(backendPayload)
    }).then(function(res) {
      if (res.ok) {
        showToast('Alamat berhasil dikirim ke backend', 'success');
      } else {
        showToast('Gagal simpan ke backend', 'error');
      }
    }).catch(function() {
      showToast('Gagal simpan ke backend', 'error');
    });

    // Close modal and show success
    closeModal();
    showToast('Address filled from map!', 'success');

    // Reset form-fill mode
    var cb = formFillCallback;
    formFillPrefix = null;
    formFillCallback = null;

    if (typeof cb === 'function') cb(selectedAddress);
    
  }

  /* ── BIND EVENTS ────────────────────────────── */
  function bindEvents() {
    var overlay = qs('#cam-overlay');
    if (!overlay) return;
    var gpsBtn = qs('#cam-btn-gps');
    if (gpsBtn) gpsBtn.addEventListener('click', handleGPS);

    var manualBtn = qs('#cam-btn-manual');
    if (manualBtn) manualBtn.addEventListener('click', handleManual);

    var recenterBtn = qs('#cam-recenter');
    if (recenterBtn) recenterBtn.addEventListener('click', handleRecenter);

    var closeBtn = qs('#cam-close');
    if (closeBtn) closeBtn.addEventListener('click', closeModal);

    var saveBtn = qs('#cam-save-btn');
    if (saveBtn) saveBtn.addEventListener('click', handleSave);
  }

  /* ────────────────────────────────────────────
     BUY IT NOW: Address confirmation modal
     ──────────────────────────────────────────── */
  var _buyNowTarget = null; // the actual button to re-click after confirmation
  var _buyNowBypassed = false; // flag to skip interceptor on re-click

  function createBuyNowModalHTML() {
    return '' +
      '<div class="cam-buynow-overlay" id="cam-buynow-overlay" aria-hidden="true" role="dialog" aria-label="Confirm delivery address">' +
        '<div class="cam-buynow-modal" role="document">' +
          '<div class="cam-handle"></div>' +
          '<div class="cam-header">' +
            '<h2 class="cam-header__title">' +
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>' +
              ' Confirm Delivery Address' +
            '</h2>' +
            '<button class="cam-close" id="cam-buynow-close" aria-label="Close">' +
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
            '</button>' +
          '</div>' +
          '<div class="cam-buynow-body">' +
            '<p style="margin:0 0 10px;font-size:13px;color:#666;">Select the address you want to deliver to before purchasing:</p>' +
            '<div class="cam-address-selector cam-buynow-selector" id="cam-buynow-address-selector">' +
              '<button type="button" class="cam-address-selector__toggle">' +
                '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:16px;height:16px;flex-shrink:0;"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>' +
                '<span class="cam-address-selector__value">Select address...</span>' +
                '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:14px;height:14px;flex-shrink:0;margin-left:auto;"><polyline points="6 9 12 15 18 9"/></svg>' +
              '</button>' +
              '<div class="cam-address-selector__dropdown">' +
                '<div class="cam-address-selector__list" id="cam-buynow-address-list"></div>' +
                '<button type="button" class="cam-address-selector__add-btn" id="cam-buynow-add-btn">' +
                  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:14px;height:14px;"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>' +
                  ' Add New Address' +
                '</button>' +
              '</div>' +
            '</div>' +
          '</div>' +
          '<div class="cam-footer" id="cam-buynow-footer" style="display:flex;gap:10px;padding:14px 16px 20px; border-radius: 10px;">' +
            '<button class="cam-btn cam-btn--secondary" id="cam-buynow-cancel" style="flex:1;min-width:0;">Cancel</button>' +
            '<button class="cam-btn cam-btn--primary" id="cam-buynow-confirm" style="flex:1;min-width:0;">' +
              ' Confirm &amp; Buy Now' +
            '</button>' +
          '</div>' +
        '</div>' +
      '</div>';
  }

  function ensureBuyNowModalExists() {
    if (qs('#cam-buynow-overlay')) return;
    var wrap = document.createElement('div');
    wrap.innerHTML = createBuyNowModalHTML();
    document.body.appendChild(wrap.firstElementChild);
    bindBuyNowEvents();
  }

  function openBuyNowModal() {
    ensureBuyNowModalExists();
    lockScroll();

    // Auto-select default address if nothing is selected yet
    if (!_selectedCartAddressId) {
      var defaultId = window._camDefaultAddressId || null;
      var addresses = window._camCustomerAddresses || [];
      if (defaultId) {
        _selectedCartAddressId = defaultId;
      } else if (addresses.length === 1) {
        _selectedCartAddressId = addresses[0].id;
      } else if (addresses.length > 0) {
        var defAddr = addresses.find(function (a) { return a.isDefault; });
        if (defAddr) _selectedCartAddressId = defAddr.id;
        else _selectedCartAddressId = addresses[0].id;
      }
    }

    var overlay = qs('#cam-buynow-overlay');
    overlay.classList.add('is-active');
    overlay.setAttribute('aria-hidden', 'false');
    renderBuyNowAddressList();
  }

  function closeBuyNowModal() {
    var overlay = qs('#cam-buynow-overlay');
    if (!overlay) return;
    overlay.classList.remove('is-active');
    overlay.setAttribute('aria-hidden', 'true');
    unlockScroll();
  }

  function renderBuyNowAddressList() {
    var listEl = qs('#cam-buynow-address-list');
    var valueEl = qs('#cam-buynow-address-selector .cam-address-selector__value');
    var selector = qs('#cam-buynow-address-selector');
    if (listEl) renderAddressList(listEl, window._camCustomerAddresses || [], valueEl, selector);
  }

  function bindBuyNowEvents() {
    var closeBtn = qs('#cam-buynow-close');
    if (closeBtn) closeBtn.addEventListener('click', closeBuyNowModal);

    var cancelBtn = qs('#cam-buynow-cancel');
    if (cancelBtn) cancelBtn.addEventListener('click', closeBuyNowModal);

  function showPageLoading(text) {
    var el = qs('#cam-page-loading');
    if (!el) {
      el = document.createElement('div');
      el.id = 'cam-page-loading';
      el.className = 'cam-page-loading';
      el.innerHTML = '<div class="cam-page-loading__spinner"></div><p class="cam-page-loading__text">' + (text || 'Processing...') + '</p>';
      document.body.appendChild(el);
    } else {
      var textEl = el.querySelector('.cam-page-loading__text');
      if (textEl) textEl.textContent = text || 'Processing...';
    }
    requestAnimationFrame(function () { el.classList.add('is-active'); });
  }

  function hidePageLoading() {
    var el = qs('#cam-page-loading');
    if (el) el.classList.remove('is-active');
  }

  var confirmBtn = qs('#cam-buynow-confirm');
    if (confirmBtn) {
      confirmBtn.addEventListener('click', function () {
        closeBuyNowModal();
        if (_buyNowTarget) {
          _buyNowBypassed = true;
          showPageLoading('Processing your order...');
          setTimeout(function () {
            _buyNowTarget.dispatchEvent(new MouseEvent('click', {
              bubbles: true,
              cancelable: true,
              view: window,
            }));
          }, 150);
        }
      });
    }

    var addBtn = qs('#cam-buynow-add-btn');
    if (addBtn) {
      addBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        closeBuyNowModal();
        // Open map picker to add new address
        map = null;
        marker = null;
        selectedAddress = { formatted: '', lat: 0, lng: 0, extra: '', district: '', city: '', province: '', zip: '', country: '' };
        formFillPrefix = null;
        formFillCallback = null;
        openModal();
        showStep('permission');
      });
    }

    // Toggle dropdown
    var toggle = qs('#cam-buynow-address-selector .cam-address-selector__toggle');
    if (toggle) {
      toggle.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var sel = qs('#cam-buynow-address-selector');
        sel.classList.toggle('is-open');
      });
    }

    // Close dropdown on outside click
    document.addEventListener('click', function (e) {
      var sel = qs('#cam-buynow-address-selector');
      if (sel && !sel.contains(e.target)) sel.classList.remove('is-open');
    });
  }

  /* ────────────────────────────────────────────
     INTERCEPT: Checkout button click
     ──────────────────────────────────────────── */
  function interceptCheckout(e) {
    var target = e.target;
    var isCheckout =
      target.classList.contains('cart-recap__checkout') ||
      target.getAttribute('name') === 'checkout' ||
      (target.closest && target.closest('.cart-recap__checkout')) ||
      (target.closest && target.closest('button[name="checkout"]'));

    // Detect Buy It Now button (Shopify dynamic checkout / payment button)
    var buyNowEl =
      (target.closest && target.closest('.shopify-payment-button__button')) ||
      (target.closest && target.closest('.shopify-payment-button'));
    var isBuyItNow = !!buyNowEl && !isCheckout;

    if (!isCheckout && !isBuyItNow) return;

    var isLoggedIn = (window._camCustomerLoggedIn === true);
    var hasAddress = (window._camCustomerHasAddress === true);

    // ── Cart checkout flow ──
    if (isCheckout) {
      // If user already has an address, don't intercept.
      // Let the packing-kayu handler take over the checkout flow.
      if (isLoggedIn && hasAddress) {
        console.log('[CAM] Customer already has address, skipping map popup.');
        return; // Don't intercept — packing kayu will handle
      }

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      e._camHandled = true; // Signal other handlers (e.g. packing-kayu) to skip

      if (!isLoggedIn) {
        // Flag so we auto-open modal after login
        try { sessionStorage.setItem(RETURN_FLAG, '1'); } catch (ex) {}
        window.location.href = '/account/login?return_url=/cart';
        return;
      }

      // Logged in but NO address → show address picker modal
      openModal();
      showStep('permission');
      return;
    }

    // ── Buy It Now flow ──
    if (isBuyItNow) {
      // Allow if already bypassed (user confirmed address in our modal)
      if (_buyNowBypassed) {
        _buyNowBypassed = false;
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      // Store the exact clicked target and its closest payment button for re-triggering
      _buyNowTarget = target;

      if (!isLoggedIn) {
        try { sessionStorage.setItem(RETURN_FLAG, 'buynow'); } catch (ex) {}
        window.location.href = '/account/login?return_url=' + encodeURIComponent(window.location.pathname + window.location.search);
        return;
      }

      if (!hasAddress) {
        // No address → show map picker. After save, page reloads and user can Buy It Now again.
        console.log('[CAM] Buy It Now: no address, showing map picker.');
        openModal();
        showStep('permission');
        return;
      }

      // Has address → show address confirmation modal before proceeding
      console.log('[CAM] Buy It Now: has address, showing address selector.');
      openBuyNowModal();
    }
  }

  /* ────────────────────────────────────────────
     AUTO-OPEN: After returning from login
     ──────────────────────────────────────────── */
  function checkAutoOpen() {
    if (window._camCustomerLoggedIn !== true) return;

    try {
      var flag = sessionStorage.getItem(RETURN_FLAG);
      if (flag === '1') {
        // Cart checkout return
        sessionStorage.removeItem(RETURN_FLAG);
        if (window._camCustomerHasAddress === true) return;
        setTimeout(function () {
          openModal();
          showStep('permission');
        }, 600);
      } else if (flag === 'buynow') {
        // Buy It Now return
        sessionStorage.removeItem(RETURN_FLAG);
        setTimeout(function () {
          if (window._camCustomerHasAddress === true) {
            openBuyNowModal();
          } else {
            openModal();
            showStep('permission');
          }
        }, 600);
      }
    } catch (ex) {}
  }

  /* ────────────────────────────────────────────
     PUBLIC API: Open map picker for form fill mode
     Usage: window._camOpenForFormFill('address-new')
     ──────────────────────────────────────────── */
  window._camOpenForFormFill = function (prefix, callback) {
    formFillPrefix = prefix || 'address-new';
    formFillCallback = callback || null;
    console.log('[CAM] Opening in form-fill mode for prefix:', formFillPrefix);

    // Reset map state for fresh pick
    map = null;
    marker = null;
    selectedAddress = { formatted: '', lat: 0, lng: 0, extra: '', district: '', city: '', province: '', zip: '', country: '' };

    openModal();
    showStep('permission');
  };

  /* ────────────────────────────────────────────
     ADDRESS SELECTOR DROPDOWN
     ──────────────────────────────────────────── */
  var _selectedCartAddressId = null;
  var _editAddressId = null; // ID of address being edited (null = adding new)
  var _suppressDropdownClose = false; // Prevent outside-click from closing dropdown (e.g. during confirm())

  function initAddressSelector() {
    var selectors = document.querySelectorAll('.cam-address-selector');
    if (!selectors || selectors.length === 0) return;

    var addresses = window._camCustomerAddresses || [];
    var defaultId = window._camDefaultAddressId || null;

    // Restore last selection from localStorage (persists across page navigations)
    try {
      var savedId = localStorage.getItem('cam_selected_address_id');
      if (savedId) _selectedCartAddressId = parseInt(savedId, 10) || savedId;
    } catch(ex) {}

    // If no saved selection, use default
    if (!_selectedCartAddressId && defaultId) {
      _selectedCartAddressId = defaultId;
    }

    // If only one address, auto-select it
    if (addresses.length === 1) {
      _selectedCartAddressId = addresses[0].id;
    }

    // Expose selected ID globally for proceedToCheckoutWithAddress
    window._camSelectedAddressId = _selectedCartAddressId;

    // Initialize each selector instance independently
    selectors.forEach(function (selector) {
      // Skip the Buy Now selector — it has its own handler inside the modal
      if (selector.classList.contains('cam-buynow-selector')) return;
      var toggle = selector.querySelector('.cam-address-selector__toggle');
      var dropdown = selector.querySelector('.cam-address-selector__dropdown');
      var listEl = selector.querySelector('.cam-address-selector__list');
      var addBtn = selector.querySelector('.cam-address-selector__add-btn');
      var valueEl = selector.querySelector('.cam-address-selector__value');

      if (!toggle || !listEl || !addBtn) return;

      // Render initial list for this instance
      renderAddressList(listEl, addresses, valueEl, selector);

      // Toggle dropdown behavior
      toggle.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        var isOpen = selector.classList.contains('is-open');

        // Close all other selectors first
        document.querySelectorAll('.cam-address-selector').forEach(function (sel) {
          sel.classList.remove('is-open');
        });
        document.querySelectorAll('.cam-address-selector__dropdown').forEach(function (dd) {
          dd.classList.remove('is-open');
        });

        if (!isOpen) {
          selector.classList.add('is-open');
          dropdown.classList.add('is-open');
        }
      });

      // Close when clicking outside
      document.addEventListener('click', function (e) {
        if (_suppressDropdownClose) return;
        if (!selector.contains(e.target) && !dropdown.contains(e.target)) {
          selector.classList.remove('is-open');
          dropdown.classList.remove('is-open');
        }
      });

      // Handle "Add New Address" click
      addBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        selector.classList.remove('is-open');
        if (dropdown) dropdown.classList.remove('is-open');
        
        // Open map picker in default (API) mode to save new address
        // Reset map state
        map = null;
        marker = null;
        selectedAddress = { formatted: '', lat: 0, lng: 0, extra: '', district: '', city: '', province: '', zip: '', country: '' };
        formFillPrefix = null; // Use API save mode
        formFillCallback = null;
  
        openModal();
        showStep('permission');
      });
    });
  }

  function renderAddressList(listEl, addresses, valueEl, rootSelector) {
    if (!listEl) return;
    listEl.innerHTML = '';

    if (addresses.length === 0) {
      listEl.innerHTML = '<div style="padding:14px;text-align:center;color:#999;font-size:13px;">No saved addresses yet</div>';
      if (valueEl) valueEl.textContent = 'Add delivery address';
      return;
    }

    // Header
    var header = document.createElement('p');
    header.className = 'cam-address-selector__list-header';
    header.textContent = 'Your available address on checkout:';
    listEl.appendChild(header);

    addresses.forEach(function (addr, idx) {
      var item = document.createElement('div');
      item.className = 'cam-address-selector__item';

      var fullAddr = addr.address1 || '';
      var detail = [addr.address2, addr.city, addr.province, addr.zip].filter(Boolean).join(', ');
      var displayText = fullAddr + (detail ? ', ' + detail : '');

      item.innerHTML =
        '<span class="cam-address-selector__item-num">' + (idx + 1) + '.</span>' +
        '<div class="cam-address-selector__item-content">' +
          '<p class="cam-address-selector__item-address">' + escapeHtml(displayText) + '</p>' +
        '</div>' +
        '<div class="cam-address-selector__item-actions">' +
          '<button type="button" class="cam-address-selector__item-edit" title="Edit address">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>' +
          '</button>' +
          '<button type="button" class="cam-address-selector__item-delete" title="Delete address">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>' +
          '</button>' +
        '</div>';

      // Edit button
      item.querySelector('.cam-address-selector__item-edit').addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        editAddress(addr);
      });

      // Delete button
      item.querySelector('.cam-address-selector__item-delete').addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        deleteAddress(addr.id);
      });

      listEl.appendChild(item);
    });
  }

  /* ── EDIT ADDRESS ──────────────────────────── */
  function editAddress(addr) {
    openEditForm(addr);
  }

  /* ── INLINE EDIT FORM ──────────────────────── */
  var CHECK_SVG = '<svg focusable="false" class="icon icon--check" viewBox="0 0 24 24" role="presentation"><path fill="currentColor" d="M9 20l-7-7 3-3 4 4L19 4l3 3z"></path></svg>';

  function filledClass(val) { return val ? ' is-filled' : ''; }
  function formField(id, label, value) {
    return '<div class="form__input-wrapper form__input-wrapper--labelled">' +
      '<input id="' + id + '" type="text" style="border: 1px solid red; border-radius:10px;" class="form__field form__field--text' + filledClass(value) + '" value="' + escapeHtml(value || '') + '">' +
      '<label for="' + id + '" class="form__floating-label">' + label + '</label>' +
    '</div>';
  }

  function createEditFormHTML(addr) {
    var isDefaultChecked = addr.isDefault ? ' checked' : '';
    return '<div class="cam-edit-form-overlay" id="cam-edit-form-overlay" aria-hidden="true">' +
      '<div class="cam-edit-form-modal">' +
        '<div class="cam-handle"></div>' +
        '<header class="modal__header">' +
          '<h3 class="modal__title heading h2">Edit address</h3>' +
          '<button class="modal__close link" id="cam-edit-form-close" aria-label="Close">' +
            '<svg focusable="false" class="icon icon--close" viewBox="0 0 24 24" role="presentation"><path fill="currentColor" fill-rule="evenodd" d="M13.414 12l4.243 4.243-1.414 1.414L12 13.414l-4.243 4.243-1.414-1.414L10.586 12 6.343 7.757l1.414-1.414L12 10.586l4.243-4.243 1.414 1.414L13.414 12z"/></path></svg>' +
          '</button>' +
        '</header>' +
        '<div class="modal__content modal__content--ios-push cam-edit-form-body">' +
          '<p class="modal__description">Please fill in the information below:</p>' +
          formField('cam-edit-company',    'Address Label',          addr.company   || '') +
          formField('cam-edit-first-name', 'First name',             addr.firstName || '') +
          formField('cam-edit-last-name',  'Last name',              addr.lastName  || '') +
          formField('cam-edit-address1',   'Address in Google Maps', addr.address1  || '') +
          formField('cam-edit-phone',      'Phone',                  addr.phone     || '') +
          '<div class="form__input-wrapper form__input-wrapper--labelled">' +
            '<div class="checkbox-wrapper">' +
              '<input type="checkbox" class="checkbox" style="border: 1px solid red;" id="cam-edit-default"' + isDefaultChecked + '>' +
              CHECK_SVG +
            '</div>' +
            '<label for="cam-edit-default">Set as default address</label>' +
          '</div>' +
          '<button type="button" class="form__submit button button--primary button--full" id="cam-edit-save-btn">Save Address</button>' +
        '</div>' +
      '</div>' +
    '</div>';
  }

  function openEditForm(addr) {
    var existing = qs('#cam-edit-form-overlay');
    if (existing) existing.remove();

    document.querySelectorAll('.cam-address-selector').forEach(function(sel) { sel.classList.remove('is-open'); });
    document.querySelectorAll('.cam-address-selector__dropdown').forEach(function(dd) { dd.classList.remove('is-open'); });

    var wrap = document.createElement('div');
    wrap.innerHTML = createEditFormHTML(addr);
    document.body.appendChild(wrap.firstElementChild);

    lockScroll();
    var overlay = qs('#cam-edit-form-overlay');
    requestAnimationFrame(function() {
      overlay.classList.add('is-active');
      overlay.setAttribute('aria-hidden', 'false');
    });

    var closeBtn = qs('#cam-edit-form-close');
    if (closeBtn) closeBtn.addEventListener('click', closeEditForm);

    overlay.addEventListener('click', function(e) {
      if (e.target === overlay) closeEditForm();
    });

    var saveBtn = qs('#cam-edit-save-btn');
    if (saveBtn) saveBtn.addEventListener('click', function() { saveEditForm(addr); });
  }

  function closeEditForm() {
    var overlay = qs('#cam-edit-form-overlay');
    if (!overlay) return;
    overlay.classList.remove('is-active');
    overlay.setAttribute('aria-hidden', 'true');
    unlockScroll();
    setTimeout(function() {
      if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
    }, 350);
  }

  function saveEditForm(addr) {
    var saveBtn = qs('#cam-edit-save-btn');
    var company   = (qs('#cam-edit-company')    || {}).value || '';
    var firstName = (qs('#cam-edit-first-name') || {}).value || '';
    var lastName  = (qs('#cam-edit-last-name')  || {}).value || '';
    var address1  = (qs('#cam-edit-address1')   || {}).value || '';
    var phone     = (qs('#cam-edit-phone')      || {}).value || '';
    var setDefault = qs('#cam-edit-default') ? qs('#cam-edit-default').checked : false;

    if (!address1.trim()) {
      showToast('Please enter an address', 'error');
      return;
    }

    var customerEmail = window._camCustomerEmail || '';
    if (!customerEmail) {
      showToast('Customer email not found', 'error');
      return;
    }

    if (saveBtn) {
      saveBtn.disabled = true;
      saveBtn.innerHTML = '<span class="cam-btn-spinner"></span> Saving...';
    }

    var payload = {
      email: customerEmail,
      addressId: String(addr.id),
      address: {
        company:   company.trim(),
        firstName: firstName.trim(),
        lastName:  lastName.trim(),
        formatted: address1.trim(),
        address1:  address1.trim(),
        phone:     phone.trim(),
        address2:  addr.address2 || '',
        city:      addr.city     || '',
        province:  addr.province || '',
        zip:       addr.zip      || '',
        country:   addr.country  || 'Indonesia',
        district:  addr.address2 || '',
        lat:       addr.lat      || 0,
        lng:       addr.lng      || 0,
      },
    };

    fetch(BACKEND_URL + '/api/address/update', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(function(res) { return res.json(); })
      .then(function(data) {
        if (data.success) {
          var afterSave = function() {
            showToast('Address updated!', 'success');
            closeEditForm();
            try { localStorage.removeItem('cam_selected_address_id'); } catch(ex) {}
            setTimeout(function() { window.location.reload(); }, 600);
          };
          if (setDefault) {
            fetch(BACKEND_URL + '/api/address/set-default', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: customerEmail, addressId: String(addr.id) }),
            }).catch(function() {}).then(afterSave);
          } else {
            afterSave();
          }
        } else {
          showToast(data.error || 'Failed to update address', 'error');
          if (saveBtn) {
            saveBtn.disabled = false;
            saveBtn.innerHTML = 'Save Address';
          }
        }
      })
      .catch(function() {
        showToast('Network error. Please try again.', 'error');
        if (saveBtn) {
          saveBtn.disabled = false;
          saveBtn.innerHTML = 'Save Address';
        }
      });
  }

  /* ── DELETE ADDRESS ────────────────────────── */
  function deleteAddress(addressId) {
    // Use a non-blocking custom confirm so the dropdown doesn't lose focus
    var confirmed = false;
    try {
      // Temporarily suppress the outside-click handler while confirm is open
      _suppressDropdownClose = true;
      confirmed = confirm('Delete this address?');
    } finally {
      // Re-enable after a tick so the confirm's residual click doesn't close the dropdown
      setTimeout(function () { _suppressDropdownClose = false; }, 100);
    }
    if (!confirmed) return;

    var customerEmail = window._camCustomerEmail || '';
    if (!customerEmail) return;

    showToast('Deleting address...', '');

    fetch(BACKEND_URL + '/api/address/delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: customerEmail, addressId: String(addressId) }),
    })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (data.success) {
          showToast('Address deleted!', 'success');
          if (window._camCustomerAddresses) {
            // Use String comparison to avoid type mismatch (number vs string IDs)
            window._camCustomerAddresses = window._camCustomerAddresses.filter(function (a) {
              return String(a.id) !== String(addressId);
            });
          }
          if (String(_selectedCartAddressId) === String(addressId)) {
            _selectedCartAddressId = null;
            window._camSelectedAddressId = null;
            try { localStorage.removeItem('cam_selected_address_id'); } catch(ex) {}
          }
          document.querySelectorAll('.cam-address-selector').forEach(function (sel) {
            var lEl = sel.querySelector('.cam-address-selector__list');
            var vEl = sel.querySelector('.cam-address-selector__value');
            if (lEl) renderAddressList(lEl, window._camCustomerAddresses || [], vEl, sel);
            // Keep the dropdown open after deletion
            sel.classList.add('is-open');
            var dd = sel.querySelector('.cam-address-selector__dropdown');
            if (dd) dd.classList.add('is-open');
          });
        } else {
          showToast(data.error || 'Failed to delete address', 'error');
        }
      })
      .catch(function () {
        showToast('Network error. Please try again.', 'error');
      });
  }

  

  /**
   * Call backend API to set address as Shopify default
   */
  function setAddressAsDefault(addressId) {
    var customerEmail = window._camCustomerEmail || '';
    if (!customerEmail) return;

    var toastHtml = '<div style="display:flex;align-items:center;gap:8px;">' +
      '<svg style="animation: spin 1s linear infinite;" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>' +
      '<div>Updating delivery address...</div></div>';
    
    showToast('Updating delivery address...', '');

    fetch(BACKEND_URL + '/api/address/set-default', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: customerEmail,
        addressId: String(addressId),
      }),
    })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (data.success) {
          showToast('Delivery address updated!', 'success');
        } else {
          showToast(data.error || 'Failed to update address', 'error');
        }
      })
      .catch(function (err) {
        showToast('Network error. Please try again.', 'error');
      });
  }

  function escapeHtml(str) {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* ── Refresh address list after saving a new address ── */
  function refreshAddressSelector() {
    // Fetch updated addresses from the customer account API
    fetch('/account/addresses.json')
      .then(function (res) { return res.json(); })
      .catch(function () { return null; })
      .then(function (data) {
        // Fallback: reload the page to get fresh addresses from Liquid
        // The Shopify customer account API might not expose addresses directly,
        // so we reload to let Liquid re-render the data
        window.location.reload();
      });
  }

  /* ── BOOTSTRAP ──────────────────────────────── */
  // Register click interceptor immediately (capturing phase, before inline scripts)
  document.addEventListener('click', interceptCheckout, true);

  // Auto-open check once DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      checkAutoOpen();
      initAddressSelector();
    });
  } else {
    checkAutoOpen();
    initAddressSelector();
  }

})();